import type { PublicPlanItem } from '@practium/features/api';
import { EmptyState } from '@practium/ui';
import { Layers } from 'lucide-react';
import { PlanCard } from '../components/PlanCard';
import styles from '../checkoutShared.module.css';

interface StepPlanSelectionProps {
	plans: PublicPlanItem[];
	isLoading: boolean;
	onSelectPlan: (item: PublicPlanItem) => void;
}

interface PlanDisplayInfo {
	item: PublicPlanItem;
	precedingPlanName: string | null;
	advantages: string[];
}

function normalizeAdvantage(text: string): string {
	return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

function buildPlanDisplayInfo(plans: PublicPlanItem[]): PlanDisplayInfo[] {
	const sorted = [...plans].sort((a, b) => a.plan.price - b.plan.price);

	return sorted.map((item, index) => {
		if (index === 0) {
			return { item, precedingPlanName: null, advantages: item.plan.advantages };
		}

		const previous = sorted[index - 1];
		const previousAdvantages = new Set(previous.plan.advantages.map(normalizeAdvantage));
		const newAdvantages = item.plan.advantages.filter(
			(advantage) => !previousAdvantages.has(normalizeAdvantage(advantage))
		);

		return {
			item,
			precedingPlanName: previous.plan.name,
			advantages: newAdvantages.length > 0 ? newAdvantages : item.plan.advantages,
		};
	});
}

export const StepPlanSelection = ({ plans, isLoading, onSelectPlan }: StepPlanSelectionProps) => {
	if (isLoading) {
		return (
			<div className={styles.loadingState}>
				<div className={styles.loadingSpinner} />
				Carregando planos...
			</div>
		);
	}

	if (!plans.length) {
		return (
			<EmptyState
				icon={<Layers size={28} />}
				title="Nenhum plano disponível"
				description="Não há planos para exibir no momento. Entre em contato com o suporte se precisar de ajuda."
			/>
		);
	}

	return (
		<div className={styles.planGrid}>
			{buildPlanDisplayInfo(plans).map(({ item, precedingPlanName, advantages }) => (
				<PlanCard
					key={item._id}
					plan={item.plan}
					advantagesOverride={advantages}
					precedingPlanName={precedingPlanName}
					onSelect={() => onSelectPlan(item)}
				/>
			))}
		</div>
	);
};
